import React, { useState, useEffect, useCallback } from 'react';
import { 
  Zap, Lightbulb, Home, Thermometer, Wind, 
  TrendingDown, BarChart2, Map, Droplet,
  Activity, DollarSign, CheckCircle, AlertTriangle, Maximize
} from 'lucide-react';

type ActiveTab = 'dashboard' | 'rooms' | 'devices' | 'analytics';

// Types for energy management data
interface Room {
  id: string;
  name: string;
  occupancyStatus: 'OCCUPIED' | 'VACANT' | 'TRANSITIONING';
  currentTemp: number;
  targetTemp: number;
  lightingStatus: 'ON' | 'OFF' | 'DIMMED';
  lightLevel: number; // 0-100
  energyUsage: number; // kWh
  occupancyHistory: OccupancyRecord[];
  area: number; // sq ft
  devices: string[]; // IDs of connected devices
  savingsMode: 'ECO' | 'COMFORT' | 'BALANCED';
  coordinates: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  };
  trafficLevel: number; // percentage
}

interface OccupancyRecord {
  timestamp: Date;
  occupied: boolean;
  occupantCount: number;
  duration: number; // minutes
  precisePlacement: {
    x: number;
    y: number;
  }[];
}

interface Device {
  id: string;
  name: string;
  type: 'HVAC' | 'LIGHTING' | 'APPLIANCE' | 'PLUG_LOAD' | 'OTHER';
  status: 'ON' | 'OFF' | 'STANDBY' | 'DIMMED';
  currentPower: number; // watts
  roomId: string;
  controlMode: 'AUTO' | 'MANUAL' | 'SCHEDULE';
  energyUsageToday: number; // kWh
  energySaved: number; // kWh saved by smart control
  lastAction: {
    timestamp: Date;
    action: string;
    trigger: 'PRESENCE' | 'SCHEDULE' | 'MANUAL' | 'PREDICTION';
  };
  schedule?: {
    start: string; // HH:MM format
    end: string;
    days: ('MON' | 'TUE' | 'WED' | 'THU' | 'FRI' | 'SAT' | 'SUN')[];
  };
}

interface EnergySavings {
  daily: number; // kWh
  weekly: number;
  monthly: number;
  yearlyCost: number; // in dollars
  percentReduction: number;
  comparisonToStandardSensors: number; // percentage better than motion sensors
  predictedSavings: number; // predicted annual kWh
  carbonReduction: number; // kg of CO2
  smartTriggers: number; // count of AI-triggered actions
}

interface PredictionModel {
  name: string;
  accuracy: number; // percentage
  triggeredActions: number;
  energySaved: number; // kWh
  nextPredictions: {
    roomId: string;
    time: Date;
    prediction: string;
    confidence: number;
  }[];
}

interface PresenceStats {
  totalOccupancyHours: number;
  vacantRooms: number;
  occupiedRooms: number;
  unnecessaryUsage: number; // kWh wasted in vacant areas
  occupancyPatterns: {
    day: string;
    pattern: number[];
    peakHours: string[];
  }[];
  footTrafficHeatmap: number[][];
  mostFrequentZone: string;
}

const PresenceAwareEnergyManagement: React.FC = () => {
  // State
  const [activeTab, setActiveTab] = useState<ActiveTab>('dashboard');
  const [rooms, setRooms] = useState<Room[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [energySavings, setEnergySavings] = useState<EnergySavings | null>(null);
  const [presenceStats, setPresenceStats] = useState<PresenceStats | null>(null);
  const [predictionModels, setPredictionModels] = useState<PredictionModel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [liveMode, setLiveMode] = useState<boolean>(false);
  
  // Helper function to format timestamps as relative time
  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 60) {
      return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    }
  };

  // Helper function to generate occupancy history
  const generateOccupancyHistory = (occupancyRate: number, isTransitArea: boolean = false) => {
    const records: OccupancyRecord[] = [];
    const now = new Date();
    
    // Generate 24 hours of data, one record per hour
    for (let i = 0; i < 24; i++) {
      const timestamp = new Date(now.getTime() - (23 - i) * 3600000);
      const hourOfDay = timestamp.getHours();
      
      // Vary occupancy based on time of day
      let occupied = Math.random() < occupancyRate;
      if ((hourOfDay >= 22 || hourOfDay <= 5) && occupancyRate < 0.7) {
        occupied = Math.random() < 0.1; // Likely vacant at night for most rooms
      }
      if ((hourOfDay >= 9 && hourOfDay <= 17) && occupancyRate > 0.7) {
        occupied = Math.random() < 0.9; // Likely occupied during day for high-use rooms
      }
      
      const occupantCount = occupied ? (isTransitArea ? 1 : Math.floor(Math.random() * 3) + 1) : 0;
      const duration = isTransitArea ? Math.floor(Math.random() * 2) + 1 : Math.floor(Math.random() * 50) + 10;
      
      const precisePlacement = [];
      if (occupied) {
        // Generate 1-3 precise placements for occupied rooms
        const placementCount = isTransitArea ? 1 : Math.floor(Math.random() * 3) + 1;
        for (let j = 0; j < placementCount; j++) {
          precisePlacement.push({
            x: Math.floor(Math.random() * 100),
            y: Math.floor(Math.random() * 100)
          });
        }
      }
      
      records.push({
        timestamp,
        occupied,
        occupantCount,
        duration,
        precisePlacement
      });
    }
    
    return records;
  };

  // Helper function to generate a heatmap
  const generateHeatmap = () => {
    const heatmap: number[][] = [];
    const size = 10; // 10x10 grid
    
    // Initialize with zeros
    for (let i = 0; i < size; i++) {
      heatmap[i] = [];
      for (let j = 0; j < size; j++) {
        // Base value
        let value = Math.floor(Math.random() * 30);
        
        // Create hotspots around certain areas
        if ((i === 3 && j === 3) || (i === 7 && j === 7) || (i === 2 && j === 8)) {
          value += 70 + Math.floor(Math.random() * 30);
        }
        
        // Create pathways between hotspots
        if ((i === 3 && j >= 3 && j <= 7) || (i >= 3 && i <= 7 && j === 7)) {
          value += 40 + Math.floor(Math.random() * 20);
        }
        
        heatmap[i][j] = value;
      }
    }
    
    return heatmap;
  };

  // Placeholder for generating mock data function
  const generateMockData = useCallback(() => {
    // Generate mock room data
    const rooms: Room[] = [
      {
        id: 'ROOM-001',
        name: 'Living Room',
        occupancyStatus: 'OCCUPIED',
        currentTemp: 72,
        targetTemp: 72,
        lightingStatus: 'ON',
        lightLevel: 80,
        energyUsage: 1.8,
        area: 320,
        devices: ['DEV-001', 'DEV-005', 'DEV-008'],
        savingsMode: 'BALANCED',
        occupancyHistory: generateOccupancyHistory(0.7), // 70% occupancy
        coordinates: { x1: 10, y1: 10, x2: 50, y2: 40 },
        trafficLevel: 80
      },
      {
        id: 'ROOM-002',
        name: 'Kitchen',
        occupancyStatus: 'VACANT',
        currentTemp: 70,
        targetTemp: 68,
        lightingStatus: 'OFF',
        lightLevel: 0,
        energyUsage: 0.5,
        area: 180,
        devices: ['DEV-002', 'DEV-006', 'DEV-009'],
        savingsMode: 'ECO',
        occupancyHistory: generateOccupancyHistory(0.35), // 35% occupancy
        coordinates: { x1: 55, y1: 10, x2: 90, y2: 40 },
        trafficLevel: 0
      },
      {
        id: 'ROOM-003',
        name: 'Master Bedroom',
        occupancyStatus: 'VACANT',
        currentTemp: 68,
        targetTemp: 68,
        lightingStatus: 'OFF',
        lightLevel: 0,
        energyUsage: 0.2,
        area: 220,
        devices: ['DEV-003', 'DEV-007'],
        savingsMode: 'COMFORT',
        occupancyHistory: generateOccupancyHistory(0.3), // 30% occupancy
        coordinates: { x1: 10, y1: 45, x2: 40, y2: 75 },
        trafficLevel: 0
      },
      {
        id: 'ROOM-004',
        name: 'Home Office',
        occupancyStatus: 'OCCUPIED',
        currentTemp: 73,
        targetTemp: 73,
        lightingStatus: 'DIMMED',
        lightLevel: 60,
        energyUsage: 2.1,
        area: 150,
        devices: ['DEV-004', 'DEV-010', 'DEV-011'],
        savingsMode: 'BALANCED',
        occupancyHistory: generateOccupancyHistory(0.8), // 80% occupancy
        coordinates: { x1: 45, y1: 45, x2: 90, y2: 75 },
        trafficLevel: 70
      },
      {
        id: 'ROOM-005',
        name: 'Hallway',
        occupancyStatus: 'TRANSITIONING',
        currentTemp: 71,
        targetTemp: 70,
        lightingStatus: 'DIMMED',
        lightLevel: 40,
        energyUsage: 0.3,
        area: 80,
        devices: ['DEV-012'],
        savingsMode: 'ECO',
        occupancyHistory: generateOccupancyHistory(0.15, true), // 15% occupancy, high transitions
        coordinates: { x1: 40, y1: 30, x2: 55, y2: 55 },
        trafficLevel: 50
      },
      {
        id: 'ROOM-006',
        name: 'Guest Bedroom',
        occupancyStatus: 'VACANT',
        currentTemp: 68,
        targetTemp: 68,
        lightingStatus: 'OFF',
        lightLevel: 0,
        energyUsage: 0.1,
        area: 160,
        devices: ['DEV-013'],
        savingsMode: 'ECO',
        occupancyHistory: generateOccupancyHistory(0.05), // 5% occupancy
        coordinates: { x1: 10, y1: 80, x2: 40, y2: 95 },
        trafficLevel: 0
      }
    ];

    // Generate mock device data
    const devices: Device[] = [
      {
        id: 'DEV-001',
        name: 'Living Room HVAC',
        type: 'HVAC',
        status: 'ON',
        currentPower: 650,
        roomId: 'ROOM-001',
        controlMode: 'AUTO',
        energyUsageToday: 7.8,
        energySaved: 2.3,
        lastAction: {
          timestamp: new Date(Date.now() - 900000), // 15 minutes ago
          action: 'Adjusted temperature to 72°',
          trigger: 'PRESENCE'
        }
      },
      {
        id: 'DEV-002',
        name: 'Kitchen HVAC',
        type: 'HVAC',
        status: 'STANDBY',
        currentPower: 50,
        roomId: 'ROOM-002',
        controlMode: 'AUTO',
        energyUsageToday: 3.2,
        energySaved: 4.1,
        lastAction: {
          timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
          action: 'Set to ECO mode',
          trigger: 'PRESENCE'
        }
      },
      {
        id: 'DEV-003',
        name: 'Master Bedroom HVAC',
        type: 'HVAC',
        status: 'STANDBY',
        currentPower: 40,
        roomId: 'ROOM-003',
        controlMode: 'AUTO',
        energyUsageToday: 2.1,
        energySaved: 3.4,
        lastAction: {
          timestamp: new Date(Date.now() - 3600000), // 1 hour ago
          action: 'Set to ECO mode',
          trigger: 'PRESENCE'
        }
      },
      {
        id: 'DEV-004',
        name: 'Office HVAC',
        type: 'HVAC',
        status: 'ON',
        currentPower: 580,
        roomId: 'ROOM-004',
        controlMode: 'AUTO',
        energyUsageToday: 8.5,
        energySaved: 1.8,
        lastAction: {
          timestamp: new Date(Date.now() - 600000), // 10 minutes ago
          action: 'Adjusted temperature to 73°',
          trigger: 'PRESENCE'
        }
      },
      {
        id: 'DEV-005',
        name: 'Living Room Lights',
        type: 'LIGHTING',
        status: 'ON',
        currentPower: 120,
        roomId: 'ROOM-001',
        controlMode: 'AUTO',
        energyUsageToday: 0.9,
        energySaved: 0.6,
        lastAction: {
          timestamp: new Date(Date.now() - 1200000), // 20 minutes ago
          action: 'Turned on lights at 80% brightness',
          trigger: 'PRESENCE'
        }
      },
      {
        id: 'DEV-006',
        name: 'Kitchen Lights',
        type: 'LIGHTING',
        status: 'OFF',
        currentPower: 0,
        roomId: 'ROOM-002',
        controlMode: 'AUTO',
        energyUsageToday: 0.4,
        energySaved: 0.8,
        lastAction: {
          timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
          action: 'Turned off lights',
          trigger: 'PRESENCE'
        }
      },
      {
        id: 'DEV-007',
        name: 'Bedroom Lights',
        type: 'LIGHTING',
        status: 'OFF',
        currentPower: 0,
        roomId: 'ROOM-003',
        controlMode: 'AUTO',
        energyUsageToday: 0.3,
        energySaved: 0.5,
        lastAction: {
          timestamp: new Date(Date.now() - 3600000), // 1 hour ago
          action: 'Turned off lights',
          trigger: 'PRESENCE'
        }
      },
      {
        id: 'DEV-008',
        name: 'Living Room TV',
        type: 'APPLIANCE',
        status: 'ON',
        currentPower: 150,
        roomId: 'ROOM-001',
        controlMode: 'MANUAL',
        energyUsageToday: 1.2,
        energySaved: 0.5,
        lastAction: {
          timestamp: new Date(Date.now() - 1500000), // 25 minutes ago
          action: 'Turned on TV',
          trigger: 'MANUAL'
        }
      },
      {
        id: 'DEV-009',
        name: 'Refrigerator',
        type: 'APPLIANCE',
        status: 'ON',
        currentPower: 80,
        roomId: 'ROOM-002',
        controlMode: 'MANUAL',
        energyUsageToday: 1.9,
        energySaved: 0.2,
        lastAction: {
          timestamp: new Date(Date.now() - 7200000), // 2 hours ago
          action: 'Optimized cooling cycle',
          trigger: 'PREDICTION'
        }
      },
      {
        id: 'DEV-010',
        name: 'Computer',
        type: 'PLUG_LOAD',
        status: 'ON',
        currentPower: 220,
        roomId: 'ROOM-004',
        controlMode: 'MANUAL',
        energyUsageToday: 1.7,
        energySaved: 0.3,
        lastAction: {
          timestamp: new Date(Date.now() - 600000), // 10 minutes ago
          action: 'No standby when occupied',
          trigger: 'PRESENCE'
        }
      },
      {
        id: 'DEV-011',
        name: 'Office Lights',
        type: 'LIGHTING',
        status: 'DIMMED',
        currentPower: 60,
        roomId: 'ROOM-004',
        controlMode: 'AUTO',
        energyUsageToday: 0.6,
        energySaved: 0.4,
        lastAction: {
          timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
          action: 'Dimmed to 60% based on natural light',
          trigger: 'PREDICTION'
        },
        schedule: {
          start: '08:00',
          end: '18:00',
          days: ['MON', 'TUE', 'WED', 'THU', 'FRI']
        }
      },
      {
        id: 'DEV-012',
        name: 'Hallway Lights',
        type: 'LIGHTING',
        status: 'DIMMED',
        currentPower: 30,
        roomId: 'ROOM-005',
        controlMode: 'AUTO',
        energyUsageToday: 0.2,
        energySaved: 0.7,
        lastAction: {
          timestamp: new Date(Date.now() - 300000), // 5 minutes ago
          action: 'Dimmed during transition',
          trigger: 'PRESENCE'
        }
      },
      {
        id: 'DEV-013',
        name: 'Guest Room HVAC',
        type: 'HVAC',
        status: 'OFF',
        currentPower: 0,
        roomId: 'ROOM-006',
        controlMode: 'AUTO',
        energyUsageToday: 0.5,
        energySaved: 5.2,
        lastAction: {
          timestamp: new Date(Date.now() - 86400000), // 1 day ago
          action: 'Turned off HVAC in unused room',
          trigger: 'PREDICTION'
        }
      }
    ];

    // Generate energy savings data
    const savings: EnergySavings = {
      daily: 8.4,
      weekly: 58.3,
      monthly: 250.2,
      yearlyCost: 730.40,
      percentReduction: 32,
      comparisonToStandardSensors: 45, // 45% better than motion sensors
      predictedSavings: 2800, // predicted annual kWh
      carbonReduction: 840, // kg of CO2
      smartTriggers: 68 // count of AI-triggered actions
    };

    // Generate presence stats
    const presence: PresenceStats = {
      totalOccupancyHours: 86,
      vacantRooms: 3,
      occupiedRooms: 2,
      unnecessaryUsage: 12.3, // kWh wasted in vacant areas
      occupancyPatterns: [
        { 
          day: 'Weekdays',
          pattern: [5, 10, 20, 15, 10, 30, 60, 90, 70, 30, 40, 65, 70, 75, 80, 85, 90, 95, 80, 70, 60, 50, 30, 15],
          peakHours: ['8:00 AM', '6:00 PM']
        },
        {
          day: 'Weekends',
          pattern: [10, 5, 5, 5, 5, 5, 20, 45, 65, 85, 90, 85, 80, 85, 80, 75, 85, 90, 85, 80, 70, 50, 40, 20],
          peakHours: ['11:00 AM', '6:00 PM']
        }
      ],
      footTrafficHeatmap: generateHeatmap(),
      mostFrequentZone: 'Living Room'
    };

    // Generate prediction models
    const predictions: PredictionModel[] = [
      {
        name: 'Occupancy Predictor',
        accuracy: 94.5,
        triggeredActions: 42,
        energySaved: 78.3,
        nextPredictions: [
          {
            roomId: 'ROOM-001',
            time: new Date(Date.now() + 1800000), // 30 minutes from now
            prediction: 'Will remain occupied',
            confidence: 0.92
          },
          {
            roomId: 'ROOM-002',
            time: new Date(Date.now() + 900000), // 15 minutes from now
            prediction: 'Will become occupied',
            confidence: 0.87
          },
          {
            roomId: 'ROOM-004',
            time: new Date(Date.now() + 7200000), // 2 hours from now
            prediction: 'Will become vacant',
            confidence: 0.79
          }
        ]
      },
      {
        name: 'HVAC Optimizer',
        accuracy: 89.2,
        triggeredActions: 23,
        energySaved: 105.8,
        nextPredictions: [
          {
            roomId: 'ROOM-001',
            time: new Date(Date.now() + 1800000), // 30 minutes from now
            prediction: 'Pre-cool before peak hour',
            confidence: 0.85
          },
          {
            roomId: 'ROOM-003',
            time: new Date(Date.now() + 7200000), // 2 hours from now
            prediction: 'Set to comfort mode before occupancy',
            confidence: 0.82
          }
        ]
      },
      {
        name: 'Lighting Intelligence',
        accuracy: 96.3,
        triggeredActions: 35,
        energySaved: 45.7,
        nextPredictions: [
          {
            roomId: 'ROOM-002',
            time: new Date(Date.now() + 1200000), // 20 minutes from now
            prediction: 'Turn on lights at 50% for dinner preparation',
            confidence: 0.94
          },
          {
            roomId: 'ROOM-005',
            time: new Date(Date.now() + 300000), // 5 minutes from now
            prediction: 'Hallway transition incoming',
            confidence: 0.91
          }
        ]
      }
    ];

    return { rooms, devices, savings, presence, predictions };
  }, []);
  
  // Load initial data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const data = generateMockData();
      setRooms(data.rooms);
      setDevices(data.devices);
      setEnergySavings(data.savings);
      setPresenceStats(data.presence);
      setPredictionModels(data.predictions);
      
      setLoading(false);
    };
    
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // Render loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-gray-600">Loading energy management data...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold">Presence-Aware Energy Management</h2>
          <p className="text-gray-600">
            Intelligent energy optimization based on precise floor sensor occupancy data
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          <div className="flex items-center">
            <span className={`mr-2 px-3 py-1 rounded-full text-xs font-medium ${liveMode ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`}>
              {liveMode ? 'LIVE' : 'STATIC'}
            </span>
            <button
              onClick={() => setLiveMode(!liveMode)}
              className={`px-3 py-1 rounded-lg text-sm font-medium ${
                liveMode ? 'bg-blue-500 text-white' : 'bg-blue-500 text-white'
              }`}
            >
              {liveMode ? 'Disable Live Updates' : 'Enable Live Updates'}
            </button>
          </div>
          <button
            onClick={() => {
              setLoading(true);
              setTimeout(() => {
                const data = generateMockData();
                setRooms(data.rooms);
                setDevices(data.devices);
                setEnergySavings(data.savings);
                setPresenceStats(data.presence);
                setPredictionModels(data.predictions);
                setLoading(false);
              }, 800);
            }}
            className="flex items-center px-3 py-1 bg-gray-100 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-200"
          >
            <div className="mr-1">↻</div> Refresh Data
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <ul className="flex flex-wrap -mb-px">
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`inline-flex items-center py-3 px-4 text-sm font-medium border-b-2 ${
                activeTab === 'dashboard'
                  ? 'text-blue-600 border-blue-500'
                  : 'border-transparent hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              <Home size={18} className="mr-2" />
              Dashboard
            </button>
          </li>
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('rooms')}
              className={`inline-flex items-center py-3 px-4 text-sm font-medium border-b-2 ${
                activeTab === 'rooms'
                  ? 'text-blue-600 border-blue-500'
                  : 'border-transparent hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              <Map size={18} className="mr-2" />
              Room Occupancy
            </button>
          </li>
          <li className="mr-2">
            <button
              onClick={() => setActiveTab('devices')}
              className={`inline-flex items-center py-3 px-4 text-sm font-medium border-b-2 ${
                activeTab === 'devices'
                  ? 'text-blue-600 border-blue-500'
                  : 'border-transparent hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              <Zap size={18} className="mr-2" />
              Smart Devices
            </button>
          </li>
          <li>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`inline-flex items-center py-3 px-4 text-sm font-medium border-b-2 ${
                activeTab === 'analytics'
                  ? 'text-blue-600 border-blue-500'
                  : 'border-transparent hover:text-gray-600 hover:border-gray-300'
              }`}
            >
              <BarChart2 size={18} className="mr-2" />
              Predictive Analytics
            </button>
          </li>
        </ul>
      </div>

      {/* Tab Content placeholder - will be filled in next step */}
      {activeTab === 'dashboard' && energySavings && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Energy Dashboard</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold flex items-center">
                <TrendingDown className="mr-2 text-blue-500" size={18} />
                Energy Savings Today
              </h4>
              <div className="mt-2 text-2xl font-bold text-blue-600">{energySavings.daily.toFixed(1)} kWh</div>
              <p className="text-sm text-gray-600">{energySavings.weekly.toFixed(1)} kWh this week</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold flex items-center">
                <DollarSign className="mr-2 text-green-500" size={18} />
                Projected Annual Savings
              </h4>
              <div className="mt-2 text-2xl font-bold text-green-600">${energySavings.yearlyCost.toFixed(2)}</div>
              <p className="text-sm text-gray-600">{energySavings.percentReduction}% reduction in energy costs</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="font-semibold flex items-center">
                <Zap className="mr-2 text-purple-500" size={18} />
                Smart Triggers
              </h4>
              <div className="mt-2 text-2xl font-bold text-purple-600">{energySavings.smartTriggers}</div>
              <p className="text-sm text-gray-600">AI-powered actions this week</p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold mb-3 flex items-center">
                <Activity className="mr-2 text-blue-500" size={18} />
                Presence vs Standard Motion Sensors
              </h4>
              <div className="relative pt-1">
                <div className="flex mb-2 items-center justify-between">
                  <div>
                    <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
                      {energySavings.comparisonToStandardSensors}% More Efficient
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-semibold inline-block text-blue-600">
                      Precise Floor Sensing
                    </span>
                  </div>
                </div>
                <div className="flex h-4 mb-4 text-xs">
                  <div className="w-1/2 bg-gray-200 rounded-l">
                    <div className="h-full text-center text-white bg-gray-500 rounded-l py-1 px-2">
                      Standard Motion Sensors
                    </div>
                  </div>
                  <div className="w-1/2 bg-blue-200 rounded-r">
                    <div className="h-full text-center text-white bg-blue-500 rounded-r py-1 px-2">
                      Precise Floor Sensors
                    </div>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  <p>Floor sensors detect actual occupancy with {energySavings.comparisonToStandardSensors}% higher accuracy than traditional motion sensors, avoiding false triggers and missed presence.</p>
                </div>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-semibold mb-3 flex items-center">
                <Droplet className="mr-2 text-green-500" size={18} />
                Environmental Impact
              </h4>
              <div className="mt-2 flex space-x-3">
                <div className="flex-1 p-3 bg-green-50 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Carbon Reduction</div>
                  <div className="text-xl font-bold text-green-600">{energySavings.carbonReduction} kg</div>
                  <div className="text-xs text-gray-500">CO₂ per year</div>
                </div>
                <div className="flex-1 p-3 bg-blue-50 rounded-lg text-center">
                  <div className="text-sm text-gray-600">Energy Saved</div>
                  <div className="text-xl font-bold text-blue-600">{energySavings.predictedSavings} kWh</div>
                  <div className="text-xs text-gray-500">Annually</div>
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-600">
                <p>Your energy savings have the same impact as planting 24 trees or removing 2 cars from the road.</p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Current Room Status</h4>
              </div>
              <div className="p-4">
                <div className="flex space-x-3">
                  <div className="flex-1 p-3 bg-green-50 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Occupied Rooms</div>
                    <div className="text-xl font-bold text-green-600">{presenceStats?.occupiedRooms || 0}</div>
                  </div>
                  <div className="flex-1 p-3 bg-blue-50 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Vacant Rooms</div>
                    <div className="text-xl font-bold text-blue-600">{presenceStats?.vacantRooms || 0}</div>
                  </div>
                  <div className="flex-1 p-3 bg-red-50 rounded-lg text-center">
                    <div className="text-sm text-gray-600">Wasted Energy</div>
                    <div className="text-xl font-bold text-red-600">{presenceStats?.unnecessaryUsage.toFixed(1) || 0} kWh</div>
                  </div>
                </div>
                <div className="mt-4">
                  <h5 className="text-sm font-medium mb-2">Rooms Currently Occupied</h5>
                  <div className="space-y-2">
                    {rooms
                      .filter(room => room.occupancyStatus === 'OCCUPIED')
                      .map(room => (
                      <div key={room.id} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <div className="flex items-center">
                          <div className="h-2 w-2 rounded-full bg-green-500 mr-2"></div>
                          <span>{room.name}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {room.devices.length} active devices
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Predictive Energy Actions</h4>
              </div>
              <div className="p-4">
                <div className="space-y-3">
                  {predictionModels
                    .flatMap(model => model.nextPredictions)
                    .sort((a, b) => a.time.getTime() - b.time.getTime())
                    .slice(0, 4)
                    .map((prediction, index) => {
                      const room = rooms.find(r => r.id === prediction.roomId);
                      return (
                        <div key={index} className="flex items-start p-3 bg-blue-50 rounded-lg">
                          <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                            <Zap size={16} className="text-blue-600" />
                          </div>
                          <div>
                            <h5 className="font-medium text-gray-900">
                              {room?.name}: {prediction.prediction}
                            </h5>
                            <p className="text-xs text-gray-500">
                              {prediction.time.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} • 
                              Confidence: {Math.round(prediction.confidence * 100)}%
                            </p>
                          </div>
                        </div>
                      );
                    })
                  }
                </div>
                
                <div className="mt-4 flex justify-between text-sm">
                  <div className="text-gray-500">Predictions based on historical patterns</div>
                  <button className="text-blue-600 hover:text-blue-800 font-medium">View All</button>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b flex justify-between items-center">
              <h4 className="font-medium">Daily Energy Usage Comparison</h4>
              <div className="flex space-x-2">
                <button className="text-xs px-2 py-1 bg-blue-500 text-white rounded">Day</button>
                <button className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded">Week</button>
                <button className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded">Month</button>
              </div>
            </div>
            <div className="p-4">
              <div className="h-64 w-full">
                {/* Mock chart - would use a real chart library in production */}
                <div className="h-full relative bg-gray-50 rounded border border-gray-200">
                  {/* Standard motion sensor line */}
                  <div className="absolute left-0 right-0 bottom-10 border-t-2 border-gray-400 border-dashed"></div>
                  <div className="absolute left-4 bottom-12 text-xs text-gray-500">Standard Sensors</div>
                  
                  {/* Floor sensor line */}
                  <div className="absolute left-0 right-0 bottom-24 border-t-2 border-blue-500"></div>
                  <div className="absolute left-4 bottom-26 text-xs text-blue-500">Floor Sensors</div>
                  
                  {/* Savings area */}
                  <div className="absolute left-4 right-4 bottom-10 h-14 bg-blue-100 bg-opacity-50"></div>
                  <div className="absolute left-1/2 bottom-16 transform -translate-x-1/2 text-xs text-blue-700 font-semibold px-2 py-1 bg-white rounded-full border border-blue-200">
                    {energySavings.percentReduction}% Savings
                  </div>
                  
                  {/* Hour markers */}
                  <div className="absolute bottom-4 left-0 right-0 flex justify-between px-4 text-xs text-gray-500">
                    <span>12AM</span>
                    <span>6AM</span>
                    <span>12PM</span>
                    <span>6PM</span>
                    <span>12AM</span>
                  </div>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                Precise floor sensors enable significant energy savings by detecting actual occupancy, not just movement, and by predicting usage patterns.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'rooms' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Room Occupancy</h3>
          
          <p className="text-gray-600 mb-6">
            Floor sensors provide precise occupancy data beyond standard motion detection, enabling smarter energy decisions.
          </p>
          
          <div className="mb-6">
            <div className="relative h-96 border border-gray-300 rounded-lg bg-gray-50 mb-4">
              {/* Floor plan visualization */}
              {rooms.map(room => (
                <div
                  key={room.id}
                  className={`absolute rounded-md border ${
                    room.occupancyStatus === 'OCCUPIED' ? 'border-green-500 bg-green-100 bg-opacity-40' : 
                    room.occupancyStatus === 'TRANSITIONING' ? 'border-yellow-500 bg-yellow-100 bg-opacity-40' : 
                    'border-gray-400 bg-gray-100 bg-opacity-40'
                  }`}
                  style={{
                    left: `${room.coordinates.x1}%`,
                    top: `${room.coordinates.y1}%`,
                    width: `${room.coordinates.x2 - room.coordinates.x1}%`,
                    height: `${room.coordinates.y2 - room.coordinates.y1}%`,
                  }}
                >
                  <div className="absolute inset-0 flex items-center justify-center flex-col">
                    <div className="bg-white bg-opacity-90 rounded px-2 py-1 text-sm font-medium">
                      {room.name}
                    </div>
                    <div className={`text-xs mt-1 px-2 py-0.5 rounded-full ${
                      room.occupancyStatus === 'OCCUPIED' ? 'bg-green-100 text-green-800' :
                      room.occupancyStatus === 'TRANSITIONING' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {room.occupancyStatus}
                    </div>
                  </div>
                  
                  {/* Show occupant locations if occupied */}
                  {room.occupancyStatus === 'OCCUPIED' && 
                    room.occupancyHistory[room.occupancyHistory.length - 1].precisePlacement.map((position, idx) => (
                      <div 
                        key={idx}
                        className="absolute h-3 w-3 rounded-full bg-green-500 shadow-lg animate-pulse"
                        style={{
                          left: `${position.x}%`,
                          top: `${position.y}%`,
                          transform: 'translate(-50%, -50%)'
                        }}
                      ></div>
                    ))
                  }
                </div>
              ))}
              
              <div className="absolute bottom-2 right-2 bg-white rounded-md shadow p-2 text-xs">
                <div className="flex items-center mb-1">
                  <div className="w-3 h-3 bg-green-100 border border-green-500 rounded-sm mr-1"></div>
                  <span>Occupied</span>
                </div>
                <div className="flex items-center mb-1">
                  <div className="w-3 h-3 bg-yellow-100 border border-yellow-500 rounded-sm mr-1"></div>
                  <span>Transitioning</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-gray-100 border border-gray-400 rounded-sm mr-1"></div>
                  <span>Vacant</span>
                </div>
              </div>
            </div>
            
            <div className="text-center mb-6">
              <p className="text-sm text-gray-600 mb-2">
                Green dots represent precise occupant positions detected by floor sensors
              </p>
              <button className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mr-2">
                <Maximize size={16} className="mr-1" />
                View Full Map
              </button>
            </div>
          </div>
          
          <div className="mb-6 overflow-hidden shadow ring-1 ring-black ring-opacity-5 rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">Room</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Traffic Level</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Energy Use</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Temp</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Light Level</th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Mode</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {rooms.map(room => (
                  <tr key={room.id}>
                    <td className="py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                      {room.name}
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        room.occupancyStatus === 'OCCUPIED' ? 'bg-green-100 text-green-800' : 
                        room.occupancyStatus === 'TRANSITIONING' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {room.occupancyStatus}
                      </span>
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2.5 mr-2">
                          <div 
                            className={`h-2.5 rounded-full ${
                              room.trafficLevel > 75 ? 'bg-red-500' : 
                              room.trafficLevel > 50 ? 'bg-yellow-500' : 
                              'bg-green-500'
                            }`}
                            style={{ width: `${room.trafficLevel}%` }}
                          ></div>
                        </div>
                        <span className="text-xs">{room.trafficLevel}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {room.energyUsage.toFixed(1)} kWh
                    </td>
                    <td className="px-3 py-4 text-sm text-gray-500">
                      {room.currentTemp}°F/{room.targetTemp}°F
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2.5 mr-2">
                          <div 
                            className="h-2.5 rounded-full bg-yellow-400"
                            style={{ width: `${room.lightLevel}%` }}
                          ></div>
                        </div>
                        <span className="text-xs">{room.lightLevel}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        room.savingsMode === 'ECO' ? 'bg-green-100 text-green-800' : 
                        room.savingsMode === 'COMFORT' ? 'bg-blue-100 text-blue-800' : 
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {room.savingsMode}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Room Occupancy Analysis</h4>
              </div>
              <div className="p-4">
                <h5 className="text-sm font-medium mb-3">Daily Occupancy Pattern: {presenceStats?.mostFrequentZone}</h5>
                <div className="h-40 relative bg-gray-50 rounded border mb-6">
                  {/* Simple visual representation of occupancy pattern */}
                  <div className="absolute inset-x-0 bottom-0 flex items-end h-32">
                    {presenceStats?.occupancyPatterns[0].pattern.map((value, i) => (
                      <div 
                        key={i} 
                        className={`flex-1 mx-0.5 ${
                          value > 75 ? 'bg-blue-500' : 
                          value > 50 ? 'bg-blue-400' : 
                          value > 25 ? 'bg-blue-300' : 
                          'bg-blue-200'
                        }`}
                        style={{ height: `${value}%` }}
                      ></div>
                    ))}
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 flex justify-between px-1 text-xxs text-gray-500">
                    <span>12AM</span>
                    <span>6AM</span>
                    <span>12PM</span>
                    <span>6PM</span>
                    <span>11PM</span>
                  </div>
                </div>
                <div className="text-sm text-gray-600">
                  <p className="mb-2">Peak occupancy: <span className="font-medium">{presenceStats?.occupancyPatterns[0].peakHours.join(', ')}</span></p>
                  <p>Understanding precise occupancy patterns allows for optimal HVAC and lighting schedules, saving up to 32% more energy compared to time-based programming.</p>
                </div>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Occupancy-Based Energy Insights</h4>
              </div>
              <div className="p-4">
                <div className="space-y-4">
                  <div className="flex items-start p-3 bg-blue-50 rounded-lg">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                      <Thermometer size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">
                        HVAC Optimization
                      </h5>
                      <p className="text-sm text-gray-600 mt-1">
                        Office HVAC can be set to ECO mode at 5:45 PM based on predicted vacancy, saving 1.2 kWh daily.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start p-3 bg-yellow-50 rounded-lg">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-yellow-100 flex items-center justify-center mr-3">
                      <Lightbulb size={16} className="text-yellow-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">
                        Smart Lighting Control
                      </h5>
                      <p className="text-sm text-gray-600 mt-1">
                        Living Room lights are dimmed to 60% during low activity periods between 2-4 PM, saving 0.3 kWh daily.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start p-3 bg-green-50 rounded-lg">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-green-100 flex items-center justify-center mr-3">
                      <AlertTriangle size={16} className="text-green-600" />
                    </div>
                    <div>
                      <h5 className="font-medium text-gray-900">
                        Unnecessary Energy Usage
                      </h5>
                      <p className="text-sm text-gray-600 mt-1">
                        Guest Bedroom is powered but unoccupied 98% of the time. Recommend setting devices to deep standby.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h4 className="font-medium">Foot Traffic Heatmap</h4>
            </div>
            <div className="p-4">
              <div className="mb-4 h-64 relative bg-gray-50 rounded border">
                {/* Heat map visualization */}
                {presenceStats?.footTrafficHeatmap.map((row, rowIndex) => (
                  <div key={rowIndex} className="flex">
                    {row.map((value, colIndex) => (
                      <div 
                        key={colIndex}
                        className="w-1/10 h-6"
                        style={{
                          backgroundColor: value > 80 ? '#ef4444' : 
                                          value > 60 ? '#f97316' : 
                                          value > 40 ? '#f59e0b' : 
                                          value > 20 ? '#84cc16' : 
                                          value > 0 ? '#22c55e' : '#f3f4f6',
                          opacity: 0.7 + (value / 300)
                        }}
                      ></div>
                    ))}
                  </div>
                ))}
                
                <div className="absolute top-2 right-2 bg-white bg-opacity-80 p-2 rounded text-xs">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-red-500 rounded-sm mr-1"></div>
                    <span className="mr-2">High Traffic</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-sm mr-1"></div>
                    <span>Low Traffic</span>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-600">
                Floor sensors create detailed foot traffic heatmaps, enabling optimized cleaning schedules, precise energy management, and targeted device control based on actual usage patterns.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'devices' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Smart Devices</h3>
          
          <p className="text-gray-600 mb-6">
            View and control your energy-saving devices that are triggered by precise floor sensor occupancy data.
          </p>
          
          <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h4 className="text-gray-800 font-medium">Connected Smart Devices</h4>
              <p className="text-sm text-gray-600">Showing {devices.length} devices connected to the floor sensor network</p>
            </div>
            <div className="mt-4 md:mt-0">
              <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                + Add Device
              </button>
            </div>
          </div>
          
          {/* Device filter */}
          <div className="mb-6 bg-gray-50 rounded-lg p-4">
            <div className="flex flex-wrap gap-3">
              <button className="px-3 py-1 bg-blue-500 text-white rounded-md text-sm">
                All Devices
              </button>
              <button className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-sm">
                HVAC
              </button>
              <button className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-sm">
                Lighting
              </button>
              <button className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-sm">
                Appliances
              </button>
              <button className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-sm">
                Plug Loads
              </button>
            </div>
          </div>
          
          {/* HVAC devices */}
          <div className="mb-6">
            <h4 className="font-medium text-gray-800 mb-3 flex items-center">
              <Thermometer size={18} className="mr-2 text-blue-500" />
              HVAC Systems
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {devices
                .filter(device => device.type === 'HVAC')
                .map(device => {
                  // Find the associated room
                  const room = rooms.find(r => r.id === device.roomId);
                  
                  return (
                    <div key={device.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className={`px-4 py-3 border-b ${
                        device.status === 'ON' ? 'bg-green-50' : 
                        device.status === 'STANDBY' ? 'bg-blue-50' : 
                        'bg-gray-50'
                      }`}>
                        <div className="flex justify-between items-center">
                          <h5 className="font-medium">{device.name}</h5>
                          <div className="flex items-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              device.status === 'ON' ? 'bg-green-100 text-green-800' : 
                              device.status === 'STANDBY' ? 'bg-blue-100 text-blue-800' : 
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {device.status}
                            </span>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Located in: {room?.name}</div>
                      </div>
                      
                      <div className="p-4">
                        <div className="mb-3">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Current Power Usage</span>
                            <span className="font-medium">{device.currentPower} W</span>
                          </div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Today's Usage</span>
                            <span className="font-medium">{device.energyUsageToday} kWh</span>
                          </div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Energy Saved</span>
                            <span className="font-medium text-green-600">{device.energySaved} kWh</span>
                          </div>
                        </div>
                        
                        <div className="border-t border-gray-100 pt-3">
                          <div className="text-xs text-gray-500 mb-1">Last Action</div>
                          <div className="text-sm">{device.lastAction.action}</div>
                          <div className="flex justify-between mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                              device.lastAction.trigger === 'PRESENCE' ? 'bg-green-100 text-green-800' : 
                              device.lastAction.trigger === 'PREDICTION' ? 'bg-purple-100 text-purple-800' : 
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {device.lastAction.trigger}
                            </span>
                            <span className="text-xs text-gray-500">{formatTimeAgo(device.lastAction.timestamp)}</span>
                          </div>
                        </div>
                        
                        <div className="mt-4 flex space-x-2">
                          <button className="flex-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded text-sm">
                            Control
                          </button>
                          <button className="flex-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-sm">
                            Schedule
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              }
            </div>
          </div>
          
          {/* Lighting devices */}
          <div className="mb-6">
            <h4 className="font-medium text-gray-800 mb-3 flex items-center">
              <Lightbulb size={18} className="mr-2 text-yellow-500" />
              Lighting Systems
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {devices
                .filter(device => device.type === 'LIGHTING')
                .map(device => {
                  // Find the associated room
                  const room = rooms.find(r => r.id === device.roomId);
                  
                  return (
                    <div key={device.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className={`px-4 py-3 border-b ${
                        device.status === 'ON' ? 'bg-yellow-50' : 
                        device.status === 'DIMMED' ? 'bg-amber-50' : 
                        'bg-gray-50'
                      }`}>
                        <div className="flex justify-between items-center">
                          <h5 className="font-medium">{device.name}</h5>
                          <div className="flex items-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              device.status === 'ON' ? 'bg-yellow-100 text-yellow-800' : 
                              device.status === 'DIMMED' ? 'bg-amber-100 text-amber-800' : 
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {device.status}
                            </span>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Located in: {room?.name}</div>
                      </div>
                      
                      <div className="p-4">
                        <div className="mb-3">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Current Power Usage</span>
                            <span className="font-medium">{device.currentPower} W</span>
                          </div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Today's Usage</span>
                            <span className="font-medium">{device.energyUsageToday} kWh</span>
                          </div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Energy Saved</span>
                            <span className="font-medium text-green-600">{device.energySaved} kWh</span>
                          </div>
                        </div>
                        
                        <div className="border-t border-gray-100 pt-3">
                          <div className="text-xs text-gray-500 mb-1">Last Action</div>
                          <div className="text-sm">{device.lastAction.action}</div>
                          <div className="flex justify-between mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                              device.lastAction.trigger === 'PRESENCE' ? 'bg-green-100 text-green-800' : 
                              device.lastAction.trigger === 'PREDICTION' ? 'bg-purple-100 text-purple-800' : 
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {device.lastAction.trigger}
                            </span>
                            <span className="text-xs text-gray-500">{formatTimeAgo(device.lastAction.timestamp)}</span>
                          </div>
                        </div>
                        
                        {/* If device has a schedule, show it */}
                        {device.schedule && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="text-xs text-gray-500 mb-1">Schedule</div>
                            <div className="text-sm">{device.schedule.start} - {device.schedule.end}</div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {device.schedule.days.map(day => (
                                <span key={day} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700">
                                  {day}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        <div className="mt-4 flex space-x-2">
                          <button className="flex-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded text-sm">
                            Control
                          </button>
                          <button className="flex-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-sm">
                            Schedule
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              }
            </div>
          </div>
          
          {/* Appliances and others - collapsed for brevity */}
          <div className="mb-6">
            <h4 className="font-medium text-gray-800 mb-3 flex items-center">
              <Zap size={18} className="mr-2 text-purple-500" />
              Appliances &amp; Other Devices
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {devices
                .filter(device => device.type !== 'HVAC' && device.type !== 'LIGHTING')
                .map(device => {
                  // Find the associated room
                  const room = rooms.find(r => r.id === device.roomId);
                  
                  return (
                    <div key={device.id} className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="px-4 py-3 border-b bg-purple-50">
                        <div className="flex justify-between items-center">
                          <h5 className="font-medium">{device.name}</h5>
                          <div className="flex items-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              device.status === 'ON' ? 'bg-green-100 text-green-800' : 
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {device.status}
                            </span>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Located in: {room?.name} • {device.type}</div>
                      </div>
                      
                      <div className="p-4">
                        <div className="mb-3">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Current Power Usage</span>
                            <span className="font-medium">{device.currentPower} W</span>
                          </div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Today's Usage</span>
                            <span className="font-medium">{device.energyUsageToday} kWh</span>
                          </div>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">Energy Saved</span>
                            <span className="font-medium text-green-600">{device.energySaved} kWh</span>
                          </div>
                        </div>
                        
                        <div className="border-t border-gray-100 pt-3">
                          <div className="text-xs text-gray-500 mb-1">Last Action</div>
                          <div className="text-sm">{device.lastAction.action}</div>
                          <div className="flex justify-between mt-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${
                              device.lastAction.trigger === 'PRESENCE' ? 'bg-green-100 text-green-800' : 
                              device.lastAction.trigger === 'PREDICTION' ? 'bg-purple-100 text-purple-800' : 
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {device.lastAction.trigger}
                            </span>
                            <span className="text-xs text-gray-500">{formatTimeAgo(device.lastAction.timestamp)}</span>
                          </div>
                        </div>
                        
                        <div className="mt-4 flex space-x-2">
                          <button className="flex-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded text-sm">
                            Control
                          </button>
                          <button className="flex-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-sm">
                            Schedule
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              }
            </div>
          </div>
          
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h4 className="font-medium">Presence-Triggered Automations</h4>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                These automations are triggered by precise floor sensor data about room occupancy:
              </p>
              
              <div className="space-y-3">
                <div className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <h5 className="font-medium">Auto-off when room vacant</h5>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Turns off lights and sets HVAC to eco mode when floor sensors detect no occupancy for 10 minutes.
                  </p>
                  <div className="flex justify-between text-xs mt-2">
                    <span className="text-gray-500">Applied to: All rooms</span>
                    <span className="text-blue-600 cursor-pointer">Edit</span>
                  </div>
                </div>
                
                <div className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <h5 className="font-medium">Hallway transition lighting</h5>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Dims hallway lights to 40% when someone is passing through but not lingering.
                  </p>
                  <div className="flex justify-between text-xs mt-2">
                    <span className="text-gray-500">Applied to: Hallway</span>
                    <span className="text-blue-600 cursor-pointer">Edit</span>
                  </div>
                </div>
                
                <div className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <h5 className="font-medium">Predictive HVAC optimization</h5>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Uses historical floor sensor data to predict room occupancy and pre-condition spaces only when needed.
                  </p>
                  <div className="flex justify-between text-xs mt-2">
                    <span className="text-gray-500">Applied to: Living Room, Home Office</span>
                    <span className="text-blue-600 cursor-pointer">Edit</span>
                  </div>
                </div>
                
                <div className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-center">
                    <h5 className="font-medium">Presence-adaptive lighting</h5>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Adjusts lighting brightness and color temperature based on precise occupant positions in the room.
                  </p>
                  <div className="flex justify-between text-xs mt-2">
                    <span className="text-gray-500">Applied to: Living Room, Home Office</span>
                    <span className="text-blue-600 cursor-pointer">Edit</span>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 text-center">
                <button className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  + Create New Automation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {activeTab === 'analytics' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Predictive Analytics</h3>
          
          <p className="text-gray-600 mb-6">
            Our AI models analyze floor sensor data to predict occupancy patterns and optimize energy use beyond what traditional systems can achieve.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {predictionModels.map(model => (
              <div key={model.name} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <h4 className="font-medium">{model.name}</h4>
                </div>
                <div className="p-4">
                  <div className="flex justify-between mb-2">
                    <div className="text-sm text-gray-600">Accuracy</div>
                    <div className="text-sm font-medium">{model.accuracy.toFixed(1)}%</div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                    <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${model.accuracy}%` }}></div>
                  </div>
                  
                  <div className="flex space-x-6 mb-3">
                    <div className="flex-1">
                      <div className="text-sm text-gray-600">Actions Triggered</div>
                      <div className="text-xl font-bold text-blue-600">{model.triggeredActions}</div>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm text-gray-600">Energy Saved</div>
                      <div className="text-xl font-bold text-green-600">{model.energySaved.toFixed(1)} kWh</div>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <h5 className="text-sm font-medium mb-2">Upcoming Predictions</h5>
                    <div className="space-y-2">
                      {model.nextPredictions.map((prediction, idx) => {
                        const room = rooms.find(r => r.id === prediction.roomId);
                        return (
                          <div key={idx} className="border border-gray-100 rounded p-2 bg-blue-50">
                            <div className="flex justify-between">
                              <div className="text-sm font-medium">{room?.name}</div>
                              <div className="text-xs text-gray-500">{prediction.time.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</div>
                            </div>
                            <p className="text-xs mt-1">{prediction.prediction}</p>
                            <div className="mt-1 flex items-center">
                              <div className="text-xxs text-gray-500 mr-1">Confidence:</div>
                              <div className="flex-grow h-1 bg-gray-200 rounded-full">
                                <div 
                                  className={`h-1 rounded-full ${
                                    prediction.confidence > 0.9 ? 'bg-green-500' : 
                                    prediction.confidence > 0.7 ? 'bg-blue-500' : 
                                    'bg-yellow-500'
                                  }`} 
                                  style={{ width: `${prediction.confidence * 100}%` }}
                                ></div>
                              </div>
                              <div className="text-xxs text-gray-500 ml-1">{Math.round(prediction.confidence * 100)}%</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">AI Prediction Advantages</h4>
              </div>
              <div className="p-4">
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                      <CheckCircle size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <h5 className="text-sm font-medium text-gray-900">Beyond Motion Detection</h5>
                      <p className="text-sm text-gray-600 mt-1">
                        Standard motion sensors can only detect movement, leading to false negatives during sedentary activities and false positives with pets. Our floor sensors detect weight, position, and subtle movements for 94% higher accuracy.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                      <CheckCircle size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <h5 className="text-sm font-medium text-gray-900">Predictive Optimization</h5>
                      <p className="text-sm text-gray-600 mt-1">
                        Our AI models learn your occupancy patterns to pre-emptively adjust HVAC and lighting settings, rather than simply reacting to presence. This proactive approach yields 32% greater energy savings.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                      <CheckCircle size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <h5 className="text-sm font-medium text-gray-900">Precision Occupant Tracking</h5>
                      <p className="text-sm text-gray-600 mt-1">
                        Our system knows exactly where people are in a room, not just that someone is present. This enables fine-grained control like dimming lights in unoccupied parts of large rooms while maintaining illumination where needed.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h4 className="font-medium">Energy Saving Predictions</h4>
              </div>
              <div className="p-4">
                <div className="flex mb-4">
                  <div className="flex-1 text-center">
                    <div className="text-sm text-gray-500 mb-1">Current Month</div>
                    <div className="text-2xl font-bold text-blue-600">
                      {energySavings?.monthly.toFixed(1)} kWh
                    </div>
                  </div>
                  <div className="flex-1 text-center">
                    <div className="text-sm text-gray-500 mb-1">Projected Annual</div>
                    <div className="text-2xl font-bold text-green-600">
                      {energySavings?.predictedSavings.toFixed(0)} kWh
                    </div>
                  </div>
                </div>
                
                <div className="relative h-48 bg-gray-50 rounded border mb-4">
                  {/* Simple bar chart visualization */}
                  <div className="absolute inset-x-0 bottom-0 h-40 flex items-end px-2">
                    <div className="flex-1 mx-1">
                      <div className="h-12 bg-gray-300 relative group">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs bg-white px-1 py-0.5 rounded border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity">
                          Standard Motion
                        </div>
                      </div>
                      <div className="text-xxs text-center mt-1">Std. Motion</div>
                    </div>
                    <div className="flex-1 mx-1">
                      <div className="h-20 bg-blue-300 relative group">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs bg-white px-1 py-0.5 rounded border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity">
                          Basic Presence
                        </div>
                      </div>
                      <div className="text-xxs text-center mt-1">Presence</div>
                    </div>
                    <div className="flex-1 mx-1">
                      <div className="h-30 bg-blue-500 relative group">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs bg-white px-1 py-0.5 rounded border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity">
                          Precise Position
                        </div>
                      </div>
                      <div className="text-xxs text-center mt-1">Position</div>
                    </div>
                    <div className="flex-1 mx-1">
                      <div className="h-40 bg-green-500 relative group">
                        <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs bg-white px-1 py-0.5 rounded border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity">
                          AI Prediction
                        </div>
                      </div>
                      <div className="text-xxs text-center mt-1">AI Prediction</div>
                    </div>
                  </div>
                </div>
                
                <div className="text-sm text-gray-600">
                  <p className="mb-2">The graph above shows energy savings per detection method:</p>
                  <ul className="list-disc pl-5 space-y-1 text-xs">
                    <li>Standard motion detection: Basic presence/absence detection</li>
                    <li>Floor-based presence detection: More accurate occupancy sensing</li>
                    <li>Precise position tracking: Targeted energy use in occupied areas</li>
                    <li>AI prediction: Anticipatory control based on learned patterns</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b">
              <h4 className="font-medium">Upcoming AI-Driven Actions</h4>
            </div>
            <div className="p-4">
              <div className="space-y-3">
                <div className="flex items-start p-3 bg-blue-50 rounded-lg">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center mr-3">
                    <Thermometer size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <div className="flex justify-between">
                      <h5 className="font-medium text-gray-900">
                        Pre-cool Home Office before arrival
                      </h5>
                      <span className="text-xs text-gray-500">8:30 AM tomorrow</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Based on your weekly pattern, our system will pre-cool your home office to your preferred temperature 15 minutes before your typical arrival time.
                    </p>
                    <div className="mt-2 flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="text-xs text-gray-500 mr-1">Confidence:</div>
                        <div className="w-20 h-1.5 bg-gray-200 rounded-full">
                          <div className="h-1.5 bg-green-500 rounded-full" style={{ width: '92%' }}></div>
                        </div>
                        <div className="text-xs text-gray-500 ml-1">92%</div>
                      </div>
                      <button className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                        Modify
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start p-3 bg-yellow-50 rounded-lg">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center mr-3">
                    <Lightbulb size={20} className="text-yellow-600" />
                  </div>
                  <div>
                    <div className="flex justify-between">
                      <h5 className="font-medium text-gray-900">
                        Gradually dim Living Room lights
                      </h5>
                      <span className="text-xs text-gray-500">9:45 PM today</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Based on your evening relaxation patterns, lights will gradually dim to 30% over 30 minutes before your typical bedtime.
                    </p>
                    <div className="mt-2 flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="text-xs text-gray-500 mr-1">Confidence:</div>
                        <div className="w-20 h-1.5 bg-gray-200 rounded-full">
                          <div className="h-1.5 bg-blue-500 rounded-full" style={{ width: '87%' }}></div>
                        </div>
                        <div className="text-xs text-gray-500 ml-1">87%</div>
                      </div>
                      <button className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                        Modify
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-start p-3 bg-green-50 rounded-lg">
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-green-100 flex items-center justify-center mr-3">
                    <Wind size={20} className="text-green-600" />
                  </div>
                  <div>
                    <div className="flex justify-between">
                      <h5 className="font-medium text-gray-900">
                        Weekend ECO Mode Activation
                      </h5>
                      <span className="text-xs text-gray-500">Friday 6:30 PM</span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Your home appears to be vacant most weekends. We'll activate ECO mode for all systems but can revert instantly if floor sensors detect your return.
                    </p>
                    <div className="mt-2 flex justify-between items-center">
                      <div className="flex items-center">
                        <div className="text-xs text-gray-500 mr-1">Confidence:</div>
                        <div className="w-20 h-1.5 bg-gray-200 rounded-full">
                          <div className="h-1.5 bg-yellow-500 rounded-full" style={{ width: '76%' }}></div>
                        </div>
                        <div className="text-xs text-gray-500 ml-1">76%</div>
                      </div>
                      <button className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                        Modify
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresenceAwareEnergyManagement; 